import React, { useState } from "react";
import { observer } from "mobx-react-lite";

interface FormData {
  nome: string;
  email: string;
  mensagem: string;
}

interface FormErrors {
  nome?: string;
  email?: string;
  mensagem?: string;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "success" | "error";
  message: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, type, message }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          position: "relative",
          minWidth: "300px",
          maxWidth: "400px",
          textAlign: "center",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            right: "10px",
            top: "10px",
            background: "none",
            border: "none",
            fontSize: "20px",
            cursor: "pointer",
            padding: "5px",
          }}
        >
          ×
        </button>
        <div
          style={{
            color: type === "success" ? "#28a745" : "#dc3545",
            fontSize: "24px",
            marginBottom: "15px",
          }}
        >
          {type === "success" ? "✓" : "✕"}
        </div>
        <p style={{ margin: "0 0 20px 0", fontSize: "16px" }}>{message}</p>
      </div>
    </div>
  );
};

export const Contato: React.FC = observer(() => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    mensagem: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [modal, setModal] = useState<{
    isOpen: boolean;
    type: "success" | "error";
    message: string;
  }>({
    isOpen: false,
    type: "success",
    message: "",
  });

  const validateField = (name: string, value: string): string | undefined => {
    if (!value.trim()) {
      return "Este campo é obrigatório";
    }
    if (name === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "E-mail inválido";
    }
    return undefined;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof FormData]);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const [sent, setSent] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce(
      (acc, key) => ({
        ...acc,
        [key]: true,
      }),
      {}
    );
    setTouched(allTouched);

    // Validate all fields
    const newErrors: FormErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key as keyof FormData]);
      if (error) newErrors[key as keyof FormErrors] = error;
    });

    setErrors(newErrors);

    // If no errors, proceed with form submission
    if (Object.keys(newErrors).length === 0) {
      try {
        const response = await fetch("/sendEmail.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.nome,
            email: formData.email,
            message: formData.mensagem,
          }),
        });
        const result = await response.json();
        if (result.status === "success") {
          setSent(true);
          setModal({
            isOpen: true,
            type: "success",
            message: "mensagem enviada!",
          });
          setFormData({ nome: "", email: "", mensagem: "" });
          setTouched({});
          setErrors({});
        } else {
          setModal({
            isOpen: true,
            type: "error",
            message:
              result.message +
              (result.errorInfo ? ` (${result.errorInfo})` : ""),
          });
        }
      } catch (error) {
        setModal({
          isOpen: true,
          type: "error",
          message: "Erro ao enviar email. Por favor, tente novamente.",
        });
      }
    }
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  };

  return (
    <div className="container-fluid bg_branco">
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        type={modal.type}
        message={modal.message}
      />
      <div className="container">
        <div className="row">
          <div className="col-md-8 col-md-push-2">
            <h2 className="titulo_25 titulo_700 text-center">Diga lá!</h2>
            <p className="text-center">
              Responderemos assim que possível (pode demorar um pouco, hehe...)
            </p>

            <div className="form_default">
              <form className="mg-bt_50" onSubmit={handleSubmit}>
                <label className="titulo_15 titulo_700">Você*</label>
                {touched.nome && errors.nome && (
                  <div
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "0.9em",
                      marginBottom: "5px",
                    }}
                  >
                    {errors.nome}
                  </div>
                )}
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`mg-bt_10 ${touched.nome && errors.nome ? "error-input" : ""
                    }`}
                  style={
                    touched.nome && errors.nome ? { borderColor: "red" } : {}
                  }
                />

                <label className="titulo_15 titulo_700">Seu E-mail*</label>
                {touched.email && errors.email && (
                  <div
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "0.9em",
                      marginBottom: "5px",
                    }}
                  >
                    {errors.email}
                  </div>
                )}
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    touched.email && errors.email ? "error-input" : ""
                  }
                  style={
                    touched.email && errors.email ? { borderColor: "red" } : {}
                  }
                />
                <label className="titulo_15 titulo_400 text-center txt_cor-id-1 mg-bt_10 dsp_blc">
                  ↑ Responderemos por aqui
                </label>

                <label className="titulo_15 titulo_700">Sua Mensagem*</label>
                {touched.mensagem && errors.mensagem && (
                  <div
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "0.9em",
                      marginBottom: "5px",
                    }}
                  >
                    {errors.mensagem}
                  </div>
                )}
                <textarea
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    touched.mensagem && errors.mensagem ? "error-input" : ""
                  }
                  style={
                    touched.mensagem && errors.mensagem
                      ? { borderColor: "red" }
                      : {}
                  }
                ></textarea>

                <input
                  className="bt bt_cor-id-3 bt_center"
                  type="submit"
                  value="Enviar Mensagem"
                />
                {sent && (
                  <div
                    style={{
                      color: "green",
                      marginTop: 16,
                      textAlign: "center",
                    }}
                  >
                    mensagem enviada!
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
